"use client";

import { useId, useState, type ReactNode } from "react";
import { motion, type Variants } from "motion/react";

type Item = { title: ReactNode; body: ReactNode };

const EASE = [0.16, 1, 0.3, 1] as const;

// Rows reveal one after another as the accordion scrolls into view
// ("delay loading"), and each panel expands/collapses smoothly.
const listV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const rowV: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

// Site-wide accordion. Uncontrolled by default; pass `open` + `onOpenChange`
// to control it from outside (e.g. the Why section syncs its image to the
// open item).
//
// Every panel body is RENDERED, open or closed, and collapsed ones are hidden
// by animating their height to zero rather than being unmounted. Unmounting
// them meant the text was not in the page at all until someone clicked, which
// cost two things worth more than the saved nodes:
//
//   - the admin's in-place editor works by stamping data-cms-key on rendered
//     elements, so every closed answer was uneditable from the preview. On the
//     solution pages that was most of the FAQ and why-choose copy.
//   - search engines never saw those answers. FAQ copy is written to be found,
//     and it was invisible on the pages that lean on it hardest.
//
// A closed panel is inert and aria-hidden, so it stays out of the tab order
// and off a screen reader's path — hidden to a reader either way, but present
// for the crawler and the editor.
export function Accordion({
  items,
  open: controlledOpen,
  onOpenChange,
}: {
  items: Item[];
  open?: number | null;
  onOpenChange?: (index: number | null) => void;
}) {
  const baseId = useId();
  const [uncontrolled, setUncontrolled] = useState<number | null>(0);
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolled;
  const setOpen = (v: number | null) => {
    onOpenChange?.(v);
    if (controlledOpen === undefined) setUncontrolled(v);
  };

  return (
    <motion.div
      className="divide-y divide-brand-line border-y border-brand-line"
      variants={listV}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
    >
      {items.map((item, i) => {
        const isOpen = open === i;
        const panelId = `${baseId}-panel-${i}`;
        const headerId = `${baseId}-header-${i}`;
        return (
          <motion.div key={i} variants={rowV}>
            <button
              type="button"
              id={headerId}
              onClick={() => setOpen(isOpen ? null : i)}
              className="flex w-full items-center justify-between gap-4 py-5 text-left"
              aria-expanded={isOpen}
              aria-controls={panelId}
            >
              <span className="text-lg font-medium text-brand-ink">
                {item.title}
              </span>
              <span className="text-2xl font-normal leading-none text-brand-accent">
                {isOpen ? "−" : "+"}
              </span>
            </button>
            <motion.div
              id={panelId}
              role="region"
              aria-labelledby={headerId}
              // initial={false} so the panel that starts open is simply open,
              // rather than animating itself down on first paint.
              initial={false}
              animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="overflow-hidden"
              aria-hidden={!isOpen}
              inert={!isOpen}
            >
              <div className="pb-6 pr-8 text-base font-normal leading-relaxed text-brand-slate">
                {item.body}
              </div>
            </motion.div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
